import React, { useCallback, useState } from 'react';
import { Block } from '../classes/Block';
import { VideoSettings } from './VideoSettings';
import { t } from '@lingui/core/macro';
import { getApiUrl } from '../utils/api';
import { useProject } from '../contexts/ProjectContext';
import { ApiBlock } from '../utils/apiTypes';

interface PngExporterProps {
  columnDefs: any[];
  sheetName: string;
  blocks: Block[];
  isEnabled: boolean;
  videoSettings: VideoSettings;
}

const PngExporter: React.FC<PngExporterProps> = ({
  columnDefs,
  sheetName,
  blocks,
  isEnabled,
  videoSettings
}) => {
  const { selectedProject } = useProject();
  const [isLoading, setIsLoading] = useState(false);
  const [width, setWidth] = useState(1920);
  const [aspectRatio, setAspectRatio] = useState("16:9");
  const [transparent, setTransparent] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const transformBlockToApiFormat = useCallback(
      (block: Block): ApiBlock => {
        const rows: string[][] = [];
        if (block.dataRows && block.dataRows.length > 0) {
          for (const dataRow of block.dataRows) {
            const rowValues: string[] = [];
            for (const colDef of columnDefs) {
              let value: string = String(dataRow[colDef.field] || '');
              
              // Special handling for logo blocks: ensure col2 is JSON encoded with scale key
              if (value && block.isLogoBlock && colDef.field === 'col2') {
                try {
                  // If value is already a JSON string, do nothing
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  const _ = JSON.parse(value);
                }
                catch {
                  const scaleValue: number = parseFloat(value);
                  value = JSON.stringify({ scale: isNaN(scaleValue) ? 100 : scaleValue });
                }
                
              }
              rowValues.push(value);
            }
            rows.push([...rowValues]);
          }
        }
        return {
          name: block.name,
          properties: {
            name: block.name,
            show_block_name: block.shouldDisplayBlockName,
            block_name_font_style: block.blockNameFont,
            block_name_font_size: block.blockNameSize,
            block_name_font_weight: block.blockNameWeight,
            block_name_color: block.blockNameColor,
            column_num: block.numColumns,
            distance_between_columns: block.marginColumn,
            value_label_gap: block.labelValueGap,
            distance_between_rows: block.distanceBetweenRows,
            distance_from_previous: block.distanceFromPreviousBlock,
            song_block: block.isSongBlock,
            single_cell: block.isSingleCell,
            credits_list: block.isCreditsList,
            credits_list_separator: block.separator,
            logo_block: block.isLogoBlock,
            label_font_name: block.labelFontName,
            label_font_size: block.labelFontSize,
            label_font_weight: block.labelFontWeight,
            label_align: block.labelAlign,
            label_vertical_align: block.labelVerticalAlign,
            label_color: block.labelFontColor,
            value_font_name: block.valueFontName,
            value_font_size: block.valueFontSize,
            value_font_weight: block.valueFontWeight,
            value_align: block.valueAlign,
            value_vertical_align: block.valueVerticalAlign,
            value_color: block.valueFontColor,
            has_separate_value_formatting: block.hasSeparateValueFormatting,
          },
          rows: rows,
        };
      },
      [columnDefs]
    );
  
  const exportToPNG = async () => {
    setIsLoading(true);
    try {
      const apiBlocks: ApiBlock[] = blocks.map((block) => transformBlockToApiFormat(block));

      const payload = {
        video_settings: {
          background_color: videoSettings.background_color,
          fps_num: videoSettings.fps_num,
          video_width: videoSettings.video_width,
          transparent: videoSettings.transparent,
          video_time: videoSettings.video_time,
          aspect_ratio: videoSettings.aspect_ratio,
          fonts: JSON.stringify(videoSettings.fonts),
          logos: JSON.stringify(videoSettings.logos),
        },
        data: apiBlocks,
        transparent,
      };


      const response = await fetch(`${getApiUrl()}/export-png?projectId=${selectedProject!.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Failed to export PNG');

      const arrayBuffer = await response.arrayBuffer();
      const blob = new Blob([arrayBuffer], { type: 'image/png' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `End_credits_${new Date().toISOString().split('T')[0]}.png`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setShowModal(false);
    } catch (error) {
      console.error('PNG export failed:', error);
      alert(t`Failed to export PNG. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <button
        onClick={() => setShowModal(true)}
        disabled={!isEnabled || isLoading}
        className="export-button"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          position: 'relative'
        }}
      >
        {t`Create PNG`}
      </button>

      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div
            style={{
              background: '#fff',
              padding: '24px',
              borderRadius: '8px',
              minWidth: '320px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
              position: 'relative'
            }}
          >
            <h3>{t`PNG Export Settings`}</h3>
            <label style={{ paddingRight: "8px"}}>
              Width (px):&nbsp;
              <select
                value={width}
                onChange={e => setWidth(Number(e.target.value))}
                disabled={isLoading}
                style={{ width: '150px' , paddingRight: '8px' }}
              >
                <option value={1920}>1920 (Full HD)</option>
                <option value={2048}>2048 (2K DCI)</option>
                <option value={3840}>3840 (4K UHD)</option>
                <option value={4096}>4096 (4K DCI)</option>
              </select>
            </label>
            <br />
            <label style={{ paddingLeft: "6px", marginTop: '12px', display: 'inline-block' }}>
              Aspect Ratio:&nbsp;
              <select
                value={aspectRatio}
                onChange={e => setAspectRatio(e.target.value)}
                disabled={isLoading}
                style={{ width: '150px' }}
              >
                <option value="16:9">16:9 (Widescreen)</option>
                <option value="4:3">4:3 (Standard)</option>
                <option value="21:9">21:9 (Ultra-wide)</option>
                <option value="1:1">1:1 (Square)</option>
              </select>
            </label>
            <br />
            <label style={{ marginTop: '12px', display: 'inline-block', paddingRight: "35px"}}>
              Transparent background:&nbsp;
              <input
                style={{marginTop: '2px'}}
                type="checkbox"
                checked={transparent}
                onChange={e => setTransparent(e.target.checked)}
                disabled={isLoading}
              />
            </label>
            <div style={{ marginTop: '24px', paddingRight: "31px", display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowModal(false)}
                disabled={isLoading}
                className="fancy-button"
                style={{ minWidth: '80px', height: '36px' }}
              >
                {t`Cancel`}
              </button>
              <button
                onClick={exportToPNG}
                disabled={isLoading}
                className="fancy-button"
                style={{
                  minWidth: '100px',
                  padding: '6px 12px',
                  height: '36px',
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                {isLoading && (
                  <div
                    className="loading-spinner"
                    style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #f3f3f3',
                      borderTop: '2px solid #3498db',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}
                  />
                )}
                {isLoading ? t`Creating PNG...` : t`Generate`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PngExporter;
